// NEW_FILE_JUSTIFIED: Section-aware policy form renderer with archived-field preservation
import { useMemo } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldRenderer } from './FieldRenderer';
import { ReadOnlyRepeatableGroup } from './ReadOnlyRepeatableGroup';
import { findMissingRequiredLabels } from '@/lib/insurance/validation';
import type {
  IBuilderField,
  IBuilderSection,
  ISectionedPolicyFormProps,
  IFieldWrapperProps,
} from '@/@types/insuranceBuilder';
import type { IFormField } from '@/@types/policyFormTemplates';

export function SectionedPolicyForm({
  sections,
  fieldValues,
  values,
  errors,
  onChange,
  disabled,
  showSoftWarnings,
  archivedFields,
  mode = 'edit',
}: ISectionedPolicyFormProps) {
  // 1. Computed / memos

  const isReadOnly = mode === 'readOnly';

  const activeFieldKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of sections) {
      for (const f of s.fields) keys.add(f.fieldKey);
    }
    return keys;
  }, [sections]);

  const archivedRows = useMemo(() => {
    if (!archivedFields || archivedFields.length === 0) return [];
    const valuesByKey = new Map(
      fieldValues.filter(fv => fv.fieldKey != null).map(fv => [fv.fieldKey as string, fv])
    );
    return archivedFields
      .filter(a => !activeFieldKeys.has(a.fieldKey))
      .flatMap(a => {
        const fv = valuesByKey.get(a.fieldKey);
        const hasValue =
          (fv?.value != null && fv.value.length > 0) ||
          (fv?.storageIds != null && fv.storageIds.length > 0);
        return hasValue ? [{ archived: a, fv }] : [];
      });
  }, [archivedFields, activeFieldKeys, fieldValues]);

  const allFlatFields = useMemo(() => sections.flatMap(s => s.fields), [sections]);

  const softWarnLabels = showSoftWarnings ? findMissingRequiredLabels(allFlatFields, values) : [];

  // 2. Primary return

  const effectiveDisabled = isReadOnly || disabled;

  return (
    <div className="space-y-4">
      {!isReadOnly && softWarnLabels.length > 0 && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-700 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-yellow-900 dark:text-yellow-200">
              Newly required field{softWarnLabels.length > 1 ? 's' : ''}:{' '}
              {softWarnLabels.join(', ')}
            </p>
            <p className="text-xs text-yellow-800 dark:text-yellow-300 mt-0.5">
              Please fill before saving.
            </p>
          </div>
        </div>
      )}

      {sections.map(section => (
        <SectionCard
          key={section.sectionKey}
          section={section}
          values={values}
          errors={!isReadOnly ? errors : undefined}
          onChange={onChange}
          disabled={effectiveDisabled}
          isReadOnly={isReadOnly}
        />
      ))}

      {archivedRows.length > 0 && (
        <section
          aria-label="Archived fields"
          className="rounded-xl border border-dashed bg-muted/40 p-5 space-y-3"
        >
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Archived fields</h3>
            <Badge variant="secondary">Read-only</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            These fields were removed from the type but kept on this policy.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {archivedRows.map(({ archived, fv }) => (
              <div key={archived.fieldKey} className="space-y-1">
                <Label className="text-xs text-muted-foreground">{archived.label}</Label>
                <Input
                  value={fv?.value ?? ''}
                  disabled
                  readOnly
                  aria-label={`${archived.label} (archived)`}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ISectionCardProps {
  // LOCAL_TYPE_JUSTIFIED: private sub-component interface — too tightly coupled to this file to extract to @types
  section: IBuilderSection;
  values: Record<string, unknown>;
  errors?: Record<string, string>;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
}

function SectionCard({
  section,
  values,
  errors,
  onChange,
  disabled,
  isReadOnly,
}: ISectionCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {section.title}
          <Badge variant="secondary" className="text-xs">
            {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
        {section.description && (
          <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className={gridClass(section.columns)}>
          {section.fields.map(field => (
            <FieldWrapper key={field.fieldKey} field={field}>
              {isReadOnly && field.fieldType === 'repeatable_group' ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{field.label}</Label>
                  <ReadOnlyRepeatableGroup field={field} value={values[field.fieldKey]} />
                </div>
              ) : (
                <FieldRenderer
                  field={adaptToFormField(field)}
                  value={values[field.fieldKey]}
                  onChange={onChange}
                  allValues={values}
                  disabled={disabled}
                />
              )}
              {errors?.[field.fieldKey] && (
                <p className="text-xs text-destructive mt-1">{errors[field.fieldKey]}</p>
              )}
            </FieldWrapper>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FieldWrapper({ field, children }: IFieldWrapperProps) {
  return <div className={field.fullWidth ? 'col-span-full' : undefined}>{children}</div>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function gridClass(cols: 1 | 2 | 3): string {
  if (cols === 3) return 'grid grid-cols-1 md:grid-cols-3 gap-4';
  if (cols === 2) return 'grid grid-cols-1 md:grid-cols-2 gap-4';
  return 'space-y-4';
}

/**
 * Maps an IBuilderField (type builder schema) to an IFormField (FieldRenderer contract).
 * TBuilderFieldType is a structural subset of TFormFieldType — all builder types are valid
 * form field types, so the cast is safe.
 */
function adaptToFormField(f: IBuilderField): IFormField {
  return {
    key: f.fieldKey,
    label: f.label,
    fieldType: f.fieldType as IFormField['fieldType'], // AS_JUSTIFIED: TBuilderFieldType is a strict subset of TFormFieldType; all builder types exist in the form type union
    required: f.required,
    placeholder: f.placeholder,
    helpText: f.helpText,
    optionsSource: f.optionsSource,
    customOptions: f.customOptions,
    dependsOnFieldKey: f.dependsOnFieldKey,
    extendedValidation: f.validation,
  };
}
